/**
 * Programmatic navigation to Clerk UserProfile custom pages.
 *
 * Clerk's `openUserProfile()` (via `useClerk()`) opens a standalone UserProfile
 * modal that does NOT include custom pages registered via `<UserButton.UserProfilePage>`.
 * Custom pages (Code Editor, Shortcuts, Postmortem, Layouts, etc.) are only
 * available through the UserButton's own profile modal.
 *
 * This helper works around that limitation by:
 *   1. Clicking the UserButton trigger (found via `[data-clerk-user-button]`)
 *   2. Waiting for the dropdown, then clicking "Manage account"
 *   3. Waiting for the profile modal, then clicking the target nav tab
 *
 * Targets elements using custom CSS classes injected via Clerk's `appearance.elements`
 * config in themed-user-button.tsx. The element descriptor keys
 * (e.g. `userButtonPopoverActionButton__manageAccount`, `navbarButton`) are part of
 * Clerk's public typed API (@clerk/shared/types ElementsConfig) — far more stable
 * than internal `.cl-*` class names which can change in CDN-hosted JS updates.
 *
 * A MutationObserver watches for each step's DOM to appear. A safety timeout
 * disconnects the observer after 5 seconds to prevent leaks.
 *
 * @param tabLabel — The exact label text of the custom page tab to navigate to
 *                    (e.g. "Layouts", "Code Editor", "Shortcuts", "Postmortem")
 */

// OUR selectors (we control these — update themed-user-button.tsx if changing):
//   [data-clerk-user-button]    — wrapper div attribute
//   .js-clerk-manage-account    — injected via appearance.elements on userButtonPopoverActionButton__manageAccount
//   .js-clerk-nav-btn           — injected via appearance.elements on navbarButton
//   tabLabel text               — set via <UserButton.UserProfilePage label="...">

/** Class injected onto the "Manage account" button via appearance.elements. */
export const CLERK_MANAGE_ACCOUNT_SELECTOR = '.js-clerk-manage-account'

/** Class injected onto navbar buttons via appearance.elements. */
export const CLERK_NAV_BTN_SELECTOR = '.js-clerk-nav-btn'

export function openUserProfileTab(tabLabel: string): void {
  // OUR selector — see components/themed-user-button.tsx data-clerk-user-button
  const trigger = document.querySelector<HTMLElement>(
    '[data-clerk-user-button] button',
  )
  if (!trigger) return
  trigger.click()

  let step: 'manage' | 'tab' = 'manage'

  function tryAdvance(obs?: MutationObserver): void {
    if (step === 'manage') {
      const manageBtn = document.querySelector<HTMLElement>(
        CLERK_MANAGE_ACCOUNT_SELECTOR,
      )
      if (manageBtn) {
        step = 'tab'
        manageBtn.click()
      }
    } else {
      const targetBtn = Array.from(
        document.querySelectorAll<HTMLElement>(CLERK_NAV_BTN_SELECTOR),
      ).find((btn) => btn.textContent?.trim() === tabLabel)
      if (targetBtn) {
        targetBtn.click()
        obs?.disconnect()
      }
    }
  }

  // Immediate check — catches synchronous renders (race condition fix)
  tryAdvance()

  const observer = new MutationObserver((_, obs) => {
    tryAdvance(obs)
  })
  observer.observe(document.body, { childList: true, subtree: true })
  // Safety cleanup — disconnect after 5s to prevent observer leaks
  setTimeout(() => observer.disconnect(), 5000)
}
